<template>
  <div class="p-8 bg-[#F8FAFC] min-h-screen font-sans text-slate-900">
    
    <header class="flex md:flex-row justify-between items-start md:items-center mb-10 gap-6">
      <div class="flex items-center gap-5">
        <div class="w-16 h-16 bg-gradient-to-br from-indigo-600 to-blue-500 rounded-2xl shadow-xl flex items-center justify-center text-white">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-7h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <div>
          <h1 class="text-3xl font-black tracking-tight text-slate-900">{{ t('title') }}</h1>
        </div>
      </div>
    </header>

    <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
      <div class="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm group hover:shadow-md transition-all">
        <p class="text-[10px] font-black uppercase text-slate-400 tracking-tighter mb-2">{{ t('counters') }}</p>
        <div class="flex items-end justify-between">
          <p class="text-4xl font-black text-slate-900">{{ counters.length }}</p>
          <span class="text-green-500 font-bold text-xs bg-green-50 px-2 py-1 rounded-lg">{{ counters.filter(c => c.active).length }} {{ t('active') }}</span>
        </div>
      </div>
      <div class="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm group hover:shadow-md transition-all">
        <p class="text-[10px] font-black uppercase text-slate-400 tracking-tighter mb-2">{{ t('totalStaff') }}</p>
        <p class="text-4xl font-black text-slate-900">{{ users.length }}</p>
      </div>
      <div class="bg-indigo-600 p-6 rounded-[2rem] shadow-lg shadow-indigo-100 group">
        <p class="text-[10px] font-black uppercase text-indigo-200 tracking-tighter mb-2 text-white/70">{{ t('available') }}</p>
        <p class="text-4xl font-black text-white">{{ users.filter(u => !u.assignedCounterId).length }}</p>
      </div>
    </div>

    <div class="flex gap-8 mb-8 border-b border-slate-200 px-4">
      <button @click="activeTab = 'counters'" :class="activeTab === 'counters' ? 'text-indigo-600 border-b-4 border-indigo-600 pb-4' : 'text-slate-400 pb-4'" class="text-sm font-black uppercase tracking-widest transition-all">
        🗄️ {{ t('tabStructure') }}
      </button>
      <button @click="activeTab = 'users'" :class="activeTab === 'users' ? 'text-indigo-600 border-b-4 border-indigo-600 pb-4' : 'text-slate-400 pb-4'" class="text-sm font-black uppercase tracking-widest transition-all">
        👥 {{ t('tabPersonnel') }}
      </button>
    </div>

    <div v-if="activeTab === 'counters'" class="grid lg:grid-cols-2 gap-6">
      <div v-for="counter in filteredCounters" :key="counter._id"
           class="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm hover:border-indigo-300 transition-all overflow-hidden flex flex-col">
        <div class="p-8 flex justify-between items-start bg-gradient-to-r from-slate-50 to-white border-b border-slate-50">
          <div class="flex items-center gap-5">
            <div class="w-12 h-12 bg-white rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center font-black text-indigo-600">
              {{ counter.priorityOrder }}
            </div>
            <div>
              <h3 class="text-xl font-black text-slate-900">{{ counter.name }}</h3>
              <div class="flex items-center gap-2 mt-1">
                <span :class="counter.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'" class="text-[9px] font-black uppercase px-2 py-0.5 rounded-full">
                  {{ counter.active ? t('statusOpen') : t('statusClosed') }}
                </span>
                <span v-if="counter.defaultQueueId" class="bg-blue-50 text-blue-600 text-[9px] font-bold px-2 py-0.5 rounded-full">
                  📋 {{ getQueueName(counter.defaultQueueId) }}
                </span>
              </div>
            </div>
          </div>
          <button @click="openEditDrawer(counter)" class="p-3 bg-white hover:bg-indigo-600 hover:text-white rounded-2xl border border-slate-100 transition-all group">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
          </button>
        </div>

        <div class="p-8 flex-1">
          <p class="text-[10px] font-black uppercase text-slate-400 mb-4 tracking-widest">{{ t('assignedTeam') }}</p>
          <div class="grid grid-cols-1 gap-3">
            <div v-for="user in getCounterUsers(counter._id)" :key="user._id" class="flex items-center justify-between p-3 bg-slate-50 rounded-2xl border border-slate-100">
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs uppercase">{{ user.firstName[0] }}{{ user.lastName[0] }}</div>
                <div>
                  <p class="text-xs font-bold">{{ user.firstName }} {{ user.lastName }}</p>
                  <p class="text-[9px] text-slate-400 font-medium">{{ user.email }}</p>
                </div>
              </div>
              <span class="text-[9px] font-black text-indigo-400 bg-white px-2 py-0.5 rounded shadow-sm border border-slate-100">{{ t('roleAgent') }}</span>
            </div>
            <div v-if="getCounterUsers(counter._id).length === 0" class="py-6 text-center border-2 border-dashed border-slate-100 rounded-2xl">
              <p class="text-xs text-slate-400 italic">{{ t('noAgent') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="activeTab === 'users'" class="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
      <div v-for="user in users" :key="user._id" class="bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm flex flex-col justify-between group hover:shadow-xl hover:translate-y-[-4px] transition-all">
        <div class="flex items-start justify-between mb-6">
          <div class="flex items-center gap-4">
            <div :class="user.role === 'AGENCY_ADMIN' ? 'bg-indigo-600' : 'bg-slate-200 text-slate-600'" class="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black">
              {{ user.firstName[0] }}
            </div>
            <div>
              <h4 class="font-black text-slate-900">{{ user.firstName }} {{ user.lastName }}</h4>
              <p class="text-xs text-slate-400 truncate max-w-[150px]">{{ user.email }}</p>
            </div>
          </div>
          <span :class="user.role === 'AGENCY_ADMIN' ? 'bg-indigo-50 text-indigo-600 border-indigo-100' : 'bg-slate-50 text-slate-500 border-slate-100'" class="px-3 py-1 rounded-full text-[9px] font-black uppercase border">
            {{ user.role === 'AGENCY_ADMIN' ? t('roleAdmin') : t('roleEmployee') }}
          </span>
        </div>

<div class="bg-slate-50 rounded-2xl p-4 mb-6">
  <label class="block text-[9px] font-black text-slate-400 uppercase mb-2">{{ t('assignLabel') }}</label>
  <select
    :value="user.assignedCounterId?._id || user.assignedCounterId || ''"
    @change="updateUserCounter(user, $event)"
    class="w-full bg-white border border-slate-200 rounded-xl text-xs font-bold py-2 px-3 focus:ring-2 focus:ring-indigo-500">
    <option value="">{{ t('awaitingAssign') }}</option>
    <option v-for="c in counters" :key="c._id" :value="c._id">{{ c.name }}</option>
  </select>
</div>

        <div class="flex items-center justify-between gap-2">
          <button @click="generateResetKey(user)" class="p-3 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all" title="Générer clé de réinitialisation">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
          </button>
        </div>
      </div>
    </div>

    <Transition name="slide-over">
      <div v-if="selectedCounter" class="fixed inset-0 z-50 flex overflow-hidden">
        <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" @click="selectedCounter = null"></div>
        <div class="relative ml-auto w-screen max-w-md bg-white shadow-2xl flex flex-col animate-slide-in">
          <div class="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <div>
              <h2 class="text-2xl font-black text-slate-900">{{ t('editCounter') }}</h2>
              <p class="text-xs text-slate-400 font-bold uppercase tracking-widest">{{ t('perfSettings') }}</p>
            </div>
            <button @click="selectedCounter = null" class="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-200 transition-all text-xl">&times;</button>
          </div>

          <div class="flex-1 overflow-y-auto p-8 space-y-8">
            <div class="space-y-4">
              <div>
                <label class="block text-[10px] font-black text-slate-400 uppercase mb-2">{{ t('counterName') }}</label>
                <input v-model="selectedCounter.name" class="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 font-bold focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-[10px] font-black text-slate-400 uppercase mb-2">{{ t('priorityOrder') }}</label>
                  <input type="number" v-model.number="selectedCounter.priorityOrder" class="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 font-bold focus:ring-2 focus:ring-indigo-500 outline-none" />
                </div>
                <div class="flex items-end pb-3">
                  <label class="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" v-model="selectedCounter.active" class="w-6 h-6 rounded-lg text-indigo-600 focus:ring-indigo-500 border-slate-300" />
                    <span class="text-sm font-bold text-slate-700">{{ t('active') }} / {{ t('statusOpen') }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="pt-8 border-t border-slate-100">
              <h3 class="text-sm font-black text-slate-900 mb-6 uppercase tracking-wider">{{ t('connectedAgents') }}</h3>
              <div class="space-y-3">
                <div v-for="user in getCounterUsers(selectedCounter._id)" :key="user._id" class="flex items-center justify-between p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-xl bg-white flex items-center justify-center font-bold text-indigo-600 shadow-sm">{{ user.firstName[0] }}</div>
                    <span class="font-bold text-sm">{{ user.firstName }} {{ user.lastName }}</span>
                  </div>
                  <button @click="unassignUser(user._id)" class="text-red-500 font-black text-[10px] hover:underline uppercase">{{ t('detach') }}</button>
                </div>
                <p v-if="getCounterUsers(selectedCounter._id).length === 0" class="text-center py-10 border-2 border-dashed border-slate-100 rounded-3xl text-slate-400 text-sm italic">{{ t('vacant') }}</p>
              </div>
            </div>
          </div>

          <div class="p-8 bg-slate-50 border-t border-slate-200 flex flex-col gap-3">
            <button @click="updateCounter" class="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all">{{ t('save') }}</button>
            <button @click="deleteCounter(selectedCounter._id)" class="w-full py-3 text-red-400 hover:text-red-600 font-bold text-xs uppercase transition-all">{{ t('deleteCounter') }}</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Modal Clé de Réinitialisation -->
    <Transition name="slide-over">
      <div v-if="resetKeyModal.show" class="fixed inset-0 z-50 flex items-center justify-center overflow-hidden">
        <div class="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity" @click="resetKeyModal.show = false"></div>
        <div class="relative bg-white rounded-[2.5rem] shadow-2xl max-w-lg w-full mx-4 p-8 animate-scale-in">
          <div class="flex items-center gap-4 mb-6">
            <div class="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white">
              <svg class="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
            </div>
            <div>
              <h2 class="text-2xl font-black text-slate-900">Clé Générée</h2>
              <p class="text-xs text-slate-400 font-bold uppercase tracking-widest">Réinitialisation de mot de passe</p>
            </div>
          </div>

          <div class="space-y-4 mb-6">
            <div class="bg-slate-50 rounded-2xl p-4 border border-slate-100">
              <label class="block text-[10px] font-black text-slate-400 uppercase mb-2">Email Utilisateur</label>
              <p class="text-sm font-bold text-slate-900">{{ resetKeyModal.email }}</p>
            </div>

            <div class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200">
              <label class="block text-[10px] font-black text-blue-600 uppercase mb-3">Clé de Réinitialisation</label>
              <div class="flex items-center justify-between bg-white rounded-xl px-4 py-3 shadow-sm">
                <p class="text-2xl font-black text-blue-600 tracking-widest">{{ resetKeyModal.key }}</p>
                <button @click="copyResetKey" class="p-2 hover:bg-blue-50 rounded-lg transition-all" title="Copier">
                  <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                </button>
              </div>
            </div>

            <div class="bg-orange-50 rounded-2xl p-4 border border-orange-100">
              <label class="block text-[10px] font-black text-orange-600 uppercase mb-2">⏰ Expire le</label>
              <p class="text-sm font-bold text-orange-900">{{ resetKeyModal.expiresAt }}</p>
            </div>
          </div>

          <div class="bg-yellow-50 rounded-2xl p-4 border border-yellow-200 mb-6">
            <div class="flex items-start gap-3">
              <span class="text-2xl">⚠️</span>
              <div>
                <p class="text-xs font-bold text-yellow-900 mb-1">Attention Sécurité</p>
                <p class="text-[10px] text-yellow-700 leading-relaxed">Communiquez cette clé de manière sécurisée. Elle expire dans 48 heures.</p>
              </div>
            </div>
          </div>

          <button @click="resetKeyModal.show = false" class="w-full py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg hover:bg-slate-700 transition-all">
            Fermer
          </button>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';

// --- CONFIGURATION API ---
// Configure l'URL de base et intercepte le token depuis le localStorage
const api = axios.create({
  baseURL: 'https://api.datafuse.fr/api/agency', // Adapte selon ton architecture
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.authorization = `Bearer ${token}`;
  }
  return config;
});

// --- ÉTAT (STATE) ---
const activeTab = ref('counters');
const counters = ref([]);
const users = ref([]);
const queues = ref([]);
const selectedCounter = ref(null);

const resetKeyModal = ref({
  show: false,
  key: '',
  email: '',
  expiresAt: ''
});

// --- TRADUCTIONS (I18N) ---
const translations = {
  fr: {
    title: "Gestion Agence",
    counters: "Guichets",
    active: "Actifs",
    totalStaff: "Effectif Total",
    available: "Disponibles",
    tabStructure: "Structure Guichets",
    tabPersonnel: "Annuaire Personnel",
    statusOpen: "Ouvert",
    statusClosed: "Fermé",
    assignedTeam: "Équipe assignée",
    roleAgent: "AGENT",
    noAgent: "Aucun agent pour le moment",
    roleAdmin: "Admin",
    roleEmployee: "Employé",
    assignLabel: "Assignation Guichet",
    awaitingAssign: "En attente d'assignation",
    addedOn: "Ajouté le 12 Jan 2026",
    editCounter: "Éditer Guichet",
    perfSettings: "Paramètres de performance",
    counterName: "Nom du poste",
    priorityOrder: "Priorité d'affichage",
    connectedAgents: "Agents Connectés",
    detach: "Détacher",
    vacant: "Poste vacant",
    save: "Enregistrer",
    deleteCounter: "Supprimer le guichet",
    msgUpdate: "Configuration mise à jour",
    msgDeleteCounter: "Supprimer ce guichet ? Les agents seront désassignés.",
    msgDeleted: "Guichet supprimé",
    msgConfirmUser: "Supprimer cet accès ?",
    msgUserRemoved: "Agent retiré",
    msgAssignUpdate: "Assignation mise à jour",
    msgAgentFreed: "Agent libéré"
  },
  de: {
    title: "Agenturverwaltung",
    counters: "Schalter",
    active: "Aktiv",
    totalStaff: "Gesamtbelegschaft",
    available: "Verfügbar",
    tabStructure: "Schalterstruktur",
    tabPersonnel: "Personalverzeichnis",
    statusOpen: "Offen",
    statusClosed: "Geschlossen",
    assignedTeam: "Zugewiesenes Team",
    roleAgent: "AGENT",
    noAgent: "Derzeit kein Agent",
    roleAdmin: "Admin",
    roleEmployee: "Mitarbeiter",
    assignLabel: "Schalterzuweisung",
    awaitingAssign: "Wartet auf Zuweisung",
    addedOn: "Hinzugefügt am 12. Jan 2026",
    editCounter: "Schalter bearbeiten",
    perfSettings: "Leistungseinstellungen",
    counterName: "Positionsname",
    priorityOrder: "Anzeigepriorität",
    connectedAgents: "Verbundene Agenten",
    detach: "Trennen",
    vacant: "Unbesetzt",
    save: "Speichern",
    deleteCounter: "Schalter löschen",
    msgUpdate: "Konfiguration aktualisiert",
    msgDeleteCounter: "Diesen Schalter löschen? Agenten werden freigestellt.",
    msgDeleted: "Schalter gelöscht",
    msgConfirmUser: "Diesen Zugriff löschen?",
    msgUserRemoved: "Agent entfernt",
    msgAssignUpdate: "Zuweisung aktualisiert",
    msgAgentFreed: "Agent freigestellt"
  },
  it: {
    title: "Gestione Agenzia",
    counters: "Sportelli",
    active: "Attivi",
    totalStaff: "Personale Totale",
    available: "Disponibili",
    tabStructure: "Struttura Sportelli",
    tabPersonnel: "Elenco Personale",
    statusOpen: "Aperto",
    statusClosed: "Chiuso",
    assignedTeam: "Squadra assegnata",
    roleAgent: "AGENTE",
    noAgent: "Nessun agente al momento",
    roleAdmin: "Admin",
    roleEmployee: "Dipendente",
    assignLabel: "Assegnazione Sportello",
    awaitingAssign: "In attesa di assegnazione",
    addedOn: "Aggiunto il 12 Gen 2026",
    editCounter: "Modifica Sportello",
    perfSettings: "Impostazioni prestazioni",
    counterName: "Nome postazione",
    priorityOrder: "Priorità di visualizzazione",
    connectedAgents: "Agenti Collegati",
    detach: "Scollegare",
    vacant: "Postazione vuota",
    save: "Salva",
    deleteCounter: "Elimina sportello",
    msgUpdate: "Configurazione aggiornata",
    msgDeleteCounter: "Eliminare lo sportello? Gli agenti saranno disassegnati.",
    msgDeleted: "Sportello eliminato",
    msgConfirmUser: "Eliminare questo accesso?",
    msgUserRemoved: "Agente rimosso",
    msgAssignUpdate: "Assegnazione aggiornata",
    msgAgentFreed: "Agente liberato"
  }
};

const currentLang = ref('fr');

const t = (key) => {
  return translations[currentLang.value]?.[key] || translations['fr'][key];
};

// --- LOGIQUE DE DONNÉES ---

const fetchData = async () => {
  try {
    const [qRes, cRes, uRes] = await Promise.all([
      api.get('/queues'),
      api.get('/counters'),
      api.get('/users')
    ]);
    queues.value = qRes.data;
    counters.value = cRes.data;
    users.value = uRes.data;
    console.log(qRes, cRes, uRes)
  } catch (err) {
    console.error("Erreur lors du chargement des données:", err);
    alert("Session expirée ou erreur serveur.");
  }
};

onMounted(async () => {
  // 1. Gestion de la langue
  const savedLang = localStorage.getItem('appLanguage');
  if (savedLang && ['fr', 'de', 'it'].includes(savedLang)) {
    currentLang.value = savedLang;
  }

  // 2. Appel de la récupération des données
  await fetchData();
});

// --- ACTIONS UTILISATEURS ---

// Assigner un agent à un guichet
const updateUserCounter = async (user, event) => {
  const counterId = event.target.value;
  
  console.log("🚀 Envoi de l'assignation...", { userId: user._id, counterId });

  try {
    // Utilisation de POST pour matcher la route backend
    const res = await api.post('/assign-agent', {
      userId: user._id,
      counterId: counterId || null // Envoie null si le select est vide
    });

    console.log(res)
    
    // Mise à jour de l'interface sans recharger toute la page
    const index = users.value.findIndex(u => u._id === user._id);
    if (index !== -1) {
      users.value[index].assignedCounterId = counterId || null;
    }
    
    // Optionnel : petit feedback visuel au lieu d'une alert
    console.log("Mise à jour réussie");
  } catch (err) {
    console.error("Erreur API:", err);
    alert(err.response?.data?.error || "Erreur de connexion au serveur");
    fetchData(); // Rafraîchir en cas d'erreur
  }
};

// Générer clé de reset
const generateResetKey = async (user) => {
  try {
    const res = await api.post(`/users/${user._id}/generate-reset-key`);
    resetKeyModal.value = {
      show: true,
      key: res.data.resetKey,
      email: res.data.userEmail,
      expiresAt: new Date(res.data.expiresAt).toLocaleString()
    };
  } catch (err) {
    alert("Erreur lors de la génération de la clé");
  }
};

const copyResetKey = () => {
  navigator.clipboard.writeText(resetKeyModal.value.key);
  alert("Clé copiée !");
};

// --- LOGIQUE GUICHETS ---

const getCounterUsers = (counterId) => {
  if (!counterId) return [];
  // On compare en transformant tout en String pour éviter les erreurs Object vs String
  return users.value.filter(u => {
    const assignedId = u.assignedCounterId?._id || u.assignedCounterId;
    return String(assignedId) === String(counterId);
  });
};

const getQueueName = (id) => {
  const q = queues.value.find(q => String(q._id) === String(id));
  return q ? q.name : 'File Standard';
};

const openEditDrawer = (counter) => {
  selectedCounter.value = { ...counter };
};

const updateCounter = async () => {
  try {
    // Route à créer si tu veux modifier le nom/priorité
    await api.patch(`/counters/${selectedCounter.value._id}`, selectedCounter.value);
    await fetchData();
    selectedCounter.value = null;
  } catch (err) {
    alert("Erreur lors de la mise à jour");
  }
};

const unassignUser = async (userId) => {
    // On réutilise la route assign-agent avec counterId null
    try {
        await api.patch('/assign-agent', { userId, counterId: null });
        await fetchData();
    } catch (err) {
        alert("Erreur lors du détachement");
    }
}

// --- FILTRES ---
const filteredCounters = computed(() => {
  return [...counters.value].sort((a, b) => a.priorityOrder - b.priorityOrder);
});
</script>

<style scoped>
.slide-over-enter-active, .slide-over-leave-active {
  transition: opacity 0.3s ease;
}
.slide-over-enter-from, .slide-over-leave-to {
  opacity: 0;
}

@keyframes slide-in {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}
.animate-slide-in {
  animation: slide-in 0.3s ease-out forwards;
}

@keyframes scale-in {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.animate-scale-in {
  animation: scale-in 0.2s ease-out forwards;
}
</style>

